import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type CollectionReference,
  where,
} from 'firebase/firestore';

import { db } from '@/firebase-config';
import type {
  SpeakerAttribution,
  TranscriptActionAnchor,
  TranscriptSegment,
  TranscriptSession,
} from '@/features/transcription/types';
import { buildProjectSubcollectionPath, type ProjectScope } from '@/features/sync/project-scope';
import { logger } from '@/utils/logger';

import type {
  SpeakerAttributionsListener,
  TranscriptActionAnchorsListener,
  TranscriptionClient,
  TranscriptSegmentsListener,
  TranscriptSessionsListener,
} from '../contracts/transcription-client';

type TranscriptSnapshot = {
  docs: Array<{ id: string; data: () => unknown }>;
};

function projectCollection<T>(scope: ProjectScope, collectionName: string): CollectionReference<T> {
  return collection(db, buildProjectSubcollectionPath(scope, collectionName)) as CollectionReference<T>;
}

function recordsFromSnapshot<T>(snapshot: TranscriptSnapshot): T[] {
  return snapshot.docs.map((docSnap) => ({
    ...(docSnap.data() as Record<string, unknown>),
    id: docSnap.id,
  }) as T);
}

export class FirebaseTranscriptionClient implements TranscriptionClient {
  subscribeTranscriptSessions(scope: ProjectScope, onUpdate: TranscriptSessionsListener) {
    const sessionsQuery = query(
      projectCollection<TranscriptSession>(scope, 'transcriptSessions'),
      orderBy('updatedAt', 'desc'),
    );

    return onSnapshot(
      sessionsQuery,
      (snapshot) => {
        onUpdate(recordsFromSnapshot<TranscriptSession>(snapshot as TranscriptSnapshot));
      },
      (error) => {
        logger.error('[FirebaseTranscriptionClient] Transcript sessions subscription error:', error);
      },
    );
  }

  subscribeTranscriptSegments(
    scope: ProjectScope,
    sessionId: string,
    onUpdate: TranscriptSegmentsListener,
  ) {
    const segmentsQuery = query(
      projectCollection<TranscriptSegment>(scope, 'transcriptSegments'),
      where('sessionId', '==', sessionId),
      orderBy('sequence', 'asc'),
    );

    return onSnapshot(
      segmentsQuery,
      (snapshot) => {
        onUpdate(recordsFromSnapshot<TranscriptSegment>(snapshot as TranscriptSnapshot));
      },
      (error) => {
        logger.error('[FirebaseTranscriptionClient] Transcript segments subscription error:', error);
      },
    );
  }

  subscribeTranscriptActionAnchors(
    scope: ProjectScope,
    sessionId: string,
    onUpdate: TranscriptActionAnchorsListener,
  ) {
    const anchorsQuery = query(
      projectCollection<TranscriptActionAnchor>(scope, 'transcriptActionAnchors'),
      where('sessionId', '==', sessionId),
      orderBy('createdAt', 'asc'),
    );

    return onSnapshot(
      anchorsQuery,
      (snapshot) => {
        onUpdate(recordsFromSnapshot<TranscriptActionAnchor>(snapshot as TranscriptSnapshot));
      },
      (error) => {
        logger.error('[FirebaseTranscriptionClient] Transcript action anchors subscription error:', error);
      },
    );
  }

  subscribeSpeakerAttributions(
    scope: ProjectScope,
    sessionId: string,
    onUpdate: SpeakerAttributionsListener,
  ) {
    const attributionsQuery = query(
      projectCollection<SpeakerAttribution>(scope, 'speakerAttributions'),
      where('sessionId', '==', sessionId),
      orderBy('speakerClusterId', 'asc'),
    );

    return onSnapshot(
      attributionsQuery,
      (snapshot) => {
        onUpdate(recordsFromSnapshot<SpeakerAttribution>(snapshot as TranscriptSnapshot));
      },
      (error) => {
        logger.error('[FirebaseTranscriptionClient] Speaker attributions subscription error:', error);
      },
    );
  }
}
